import { useState, useRef, useEffect } from 'react';
import { cn } from '@/utils';
import { SignDetail, SignCardList } from './Sign';
import NoIcon from './NoIcon';
import YesIcon from './YesIcon';

interface ToggleSwitchProps {
  defaultValue?: 'LOW' | 'HIGH' | 'NONE';
  onChange?: (value: 'LOW' | 'HIGH' | 'NONE') => void;
  className?: string;
  fontSize?: number;
  disabled?: boolean;
  isCardList?: boolean;
}

export default function ToggleSwitch({
  defaultValue = 'HIGH',
  onChange,
  className,
  fontSize = 12,
  disabled = false,
  isCardList = true,
}: ToggleSwitchProps) {
  const [value, setValue] = useState<'LOW' | 'HIGH' | 'NONE'>(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = (newValue: 'LOW' | 'HIGH' | 'NONE') => {
    if (disabled) return;
    setValue(newValue);
    onChange?.(newValue);
  };

  return (
    <div className={cn('flex flex-col items-center overflow-hidden w-full h-full rounded-sm font-pressura-mono', className)} ref={ref}>
      <div className="flex w-full h-full bg-gradient-to-br from-[#181a1b] to-[#1e1f21] rounded-sm border border-[rgba(255,255,255,0.05)]">
        <button
          className={cn(
            'rounded-sm',
            'relative',
            'flex items-center justify-center',
            'flex-1 text-center font-bold transition-colors',
            'bg-gradient-to-br',
            disabled ? 'cursor-default' : '',
            value === 'LOW' ? 'bg-gradient-to-br from-[#fe5b27] to-[#c64219] text-white border border-[#1A1B1C]' : 'text-zinc-500',
          )}
          onClick={() => handleToggle('LOW')}
        >
          <span className="inline-flex h-2 w-2 absolute left-1 top-1">{value === 'LOW' ? <YesIcon /> : <NoIcon />}</span>
          <div
            className={cn(
              'flex items-center justify-center self-stretch text-center font-normal uppercase leading-[12px]',
              `text-[${fontSize}px]`,
            )}
          >
            Low
          </div>
        </button>
        <button
          className={cn(
            'rounded-sm',
            'relative',
            'flex items-center justify-center',
            'flex-1 text-center font-bold transition-colors',
            'bg-gradient-to-br',
            disabled ? 'cursor-default' : '',
            value === 'HIGH' ? 'from-[#fe5b27] to-[#c64219] text-white border border-[#1A1B1C]' : 'text-zinc-500',
          )}
          onClick={() => handleToggle('HIGH')}
        >
          <span className="inline-flex h-2 w-2 absolute left-1 top-1">{value === 'HIGH' ? <YesIcon /> : <NoIcon />}</span>
          <div
            className={cn(
              'flex items-center justify-center self-stretch text-center font-normal uppercase leading-[12px]',
              `text-[${fontSize}px]`,
            )}
          >
            HIGH
          </div>
        </button>
      </div>
      {isCardList ? <SignCardList width={ref.current?.clientWidth} className="mt-2" /> : <SignDetail />}
      <div className={cn('text-white font-pressura-mono uppercase', `text-[${fontSize}px]`)}>Strength</div>
    </div>
  );
}
